import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MemberUsage, UsageBucket, UsageOverview } from '@shared/types';
import { ROLE_LABELS } from '@shared/roles';
import {
  USAGE_RANGE_LABELS,
  USAGE_RANGE_PRESETS,
  presetToRange,
  type UsageRangePreset,
} from '@shared/usage';
import { formatDateTime, relativeTime } from '@shared/format';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { Badge, QualityBar } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner, Stat } from '../../components/Card';
import { DataTable, type Column } from '../../components/Table';
import { api, errorMessage } from '../../lib/api';

/**
 * 资源用量（仅管理员）。
 *
 * 这张表的用途是**算账与看配额**：这个月烧了多少、谁在烧、要不要调额度。
 * 它**不是**绩效评价依据 —— 平台其余部分刻意不做人均产出与排名，
 * 这里是唯一按人展示数字的地方，所以口径必须写清楚，避免被当成考核表来读。
 *
 * 三个容易读错的地方，界面上都做了显式处理：
 *
 * 1. **总额里没有重复累加推理 token**。模型返回的 usage 中
 *    `reasoning_tokens` 本就嵌在 `completion_tokens` 内，它是子集不是并列项。
 *    表里单列出来只是为了让「成本花在思考上」这件事可见。
 * 2. **token 与请求次数并列**。两者互不可推导：同样一万 token 可能是 1 次大研判，
 *    也可能是 3 次短请求。
 * 3. **未计量的调用是「未知」而不是「0 消耗」**。网络错误、超时、401 这些失败
 *    发生在收到响应之前，响应里没有 usage 块，因此只计次数。表里用徽标标出来，
 *    否则「0 token 但调用很多次」看起来像 bug，而实际上它是有意义的缺口。
 */

function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString('zh-CN');
}

/** 未计量的次数。只看「有请求但没拿到 usage」的部分。 */
function unmeteredOf(bucket: UsageBucket): number {
  return Math.max(0, bucket.requests - bucket.meteredRequests);
}

export function UsagePage() {
  const [preset, setPreset] = useState<UsageRangePreset>('this_month');
  const [usage, setUsage] = useState<UsageOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (next: UsageRangePreset) => {
    setLoading(true);
    try {
      const { from, to } = presetToRange(next);
      const result = await api.get<UsageOverview>('/api/usage', {
        query: { from: from ?? '', to: to ?? '' },
      });
      setUsage(result);
      setError(null);
    } catch (fetchError) {
      setError(errorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(preset);
  }, [load, preset]);

  const unmeteredTotal = usage ? unmeteredOf(usage.total) : 0;
  const coverage =
    usage && usage.total.requests > 0 ? usage.total.meteredRequests / usage.total.requests : null;
  const maxDailyTokens = useMemo(
    () => Math.max(1, ...(usage?.byDay ?? []).map((day) => day.totalTokens)),
    [usage],
  );

  const columns: Column<MemberUsage>[] = [
    {
      key: 'name',
      header: '成员',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-fg">{row.name}</span>
          <span className="text-[10px] text-fg-subtle">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: '角色',
      width: '90px',
      render: (row) => <Badge tone={row.role === 'admin' ? 'accent' : 'neutral'}>{ROLE_LABELS[row.role]}</Badge>,
    },
    {
      key: 'requests',
      header: '请求次数',
      align: 'right',
      width: '130px',
      title: '发起研判的总次数。未计量的调用也计入这里。',
      render: (row) => {
        const unmetered = unmeteredOf(row);
        return (
          <div className="flex items-center justify-end gap-1.5">
            <span className="tabular text-fg">{row.requests}</span>
            {unmetered > 0 ? (
              <Badge tone="caution" title={`其中 ${unmetered} 次未收到模型响应，只计次数、token 未知`}>
                未计量 {unmetered}
              </Badge>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'promptTokens',
      header: '输入 token',
      align: 'right',
      width: '110px',
      hideOnMobile: true,
      render: (row) => <span className="tabular text-fg-muted">{formatTokens(row.promptTokens)}</span>,
    },
    {
      key: 'completionTokens',
      header: '输出 token',
      align: 'right',
      width: '110px',
      hideOnMobile: true,
      render: (row) => <span className="tabular text-fg-muted">{formatTokens(row.completionTokens)}</span>,
    },
    {
      key: 'reasoningTokens',
      header: '其中思考',
      align: 'right',
      width: '110px',
      hideOnMobile: true,
      title: '输出 token 中属于模型思考过程的部分。它是输出 token 的子集，不额外累加。',
      render: (row) => <span className="tabular text-fg-subtle">{formatTokens(row.reasoningTokens)}</span>,
    },
    {
      key: 'totalTokens',
      header: '合计 token',
      align: 'right',
      width: '110px',
      title: '输入 + 输出。思考 token 已包含在输出内，不重复计算。',
      render: (row) => <span className="tabular font-medium text-fg">{formatTokens(row.totalTokens)}</span>,
    },
    {
      key: 'lastRunAt',
      header: '最近一次',
      align: 'right',
      width: '110px',
      hideOnMobile: true,
      render: (row) =>
        row.lastRunAt ? (
          <span className="text-[11px] text-fg-subtle" title={formatDateTime(row.lastRunAt)}>
            {relativeTime(row.lastRunAt)}
          </span>
        ) : (
          <span className="text-[11px] text-fg-subtle">—</span>
        ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="资源用量"
        description="按成员统计 AI 研判消耗的模型额度，用于算账与判断配额是否够用。"
      />

      <Card>
        <CardBody className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">统计范围</span>
            <div className="flex gap-1.5">
              {USAGE_RANGE_PRESETS.map((item) => (
                <Button
                  key={item}
                  size="sm"
                  variant={preset === item ? 'primary' : 'ghost'}
                  onClick={() => setPreset(item)}
                  disabled={loading && preset === item}
                >
                  {USAGE_RANGE_LABELS[item]}
                </Button>
              ))}
            </div>
            {usage ? (
              <span className="ml-auto text-[11px] text-fg-subtle">
                {usage.range.from ? `自 ${formatDateTime(usage.range.from)}` : '自平台建立以来'}
                {' · '}
                统计于 {formatDateTime(usage.generatedAt)}
              </span>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {loading && !usage ? (
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 text-sm text-fg-muted">
              <Spinner /> 正在统计…
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && !usage ? (
        <Card>
          <CardBody>
            <EmptyState title="暂时拿不到用量数据" description={error ?? '请稍后重试。'} />
          </CardBody>
        </Card>
      ) : null}

      {usage ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardBody>
                <Stat label="合计 token" value={formatTokens(usage.total.totalTokens)} hint="输入 + 输出" />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat
                  label="请求次数"
                  value={usage.total.requests}
                  hint={`成功 ${usage.total.succeeded} · 失败 ${usage.total.failed}`}
                />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat
                  label="其中思考 token"
                  value={formatTokens(usage.total.reasoningTokens)}
                  hint="已含在输出 token 内，不重复累加"
                />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat
                  label="参与人数"
                  value={usage.members.filter((member) => member.requests > 0).length}
                  tone="info"
                  hint={`共 ${usage.members.length} 个账号`}
                />
              </CardBody>
            </Card>
          </div>

          {unmeteredTotal > 0 ? (
            <Card>
              <CardBody className="flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
                <Badge tone="caution">未计量 {unmeteredTotal} 次</Badge>
                <span className="flex-1 leading-relaxed">
                  这 {unmeteredTotal} 次调用没有拿到模型响应（网络错误、超时或鉴权失败），
                  因此只计入请求次数、token 未知。它们<strong>不是</strong>零消耗，所以上面的合计 token
                  是这段范围内的<strong>下限</strong>
                  {coverage !== null ? `（已计量 ${Math.round(coverage * 100)}% 的调用）` : ''}。
                </span>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle subtitle="每根柱子是一天（UTC），高度按当天的合计 token 归一">用量趋势</CardTitle>
            </CardHeader>
            <CardBody>
              {usage.byDay.length === 0 ? (
                <EmptyState title="这段时间没有调用记录" description="换个统计范围试试。" />
              ) : (
                <div className="flex flex-col gap-2">
                  {/* 天数可能很多（「全部」范围），横向滚动而不是压缩，保证每根柱子都还能读 */}
                  <div className="flex items-end gap-1 overflow-x-auto pb-1">
                    {usage.byDay.map((day) => (
                      <div
                        key={day.day}
                        className="flex min-w-0 flex-1 flex-col items-center gap-1"
                        style={{ minWidth: '14px' }}
                        title={`${day.day}（UTC）\n合计 ${day.totalTokens.toLocaleString('zh-CN')} token\n请求 ${day.requests} 次${unmeteredOf(day) > 0 ? `（未计量 ${unmeteredOf(day)} 次）` : ''}`}
                      >
                        <div className="flex h-24 w-full items-end">
                          <div
                            className="w-full rounded-t bg-accent/70"
                            style={{ height: `${Math.max(2, Math.round((day.totalTokens / maxDailyTokens) * 100))}%` }}
                          />
                        </div>
                        <span className="w-full truncate text-center text-[9px] text-fg-subtle">
                          {day.day.slice(5)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-fg-subtle">
                    只列出有调用的日期，空缺的日子不会被补成零 —— 「没有数据」和「用量为零」不是一回事。
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle subtitle="按合计 token 降序。含未发起过研判的账号（各项为 0）">
                每人用量
              </CardTitle>
            </CardHeader>
            <CardBody>
              <DataTable
                rows={usage.members}
                columns={columns}
                getRowId={(row) => row.userId}
                loading={loading}
                emptyTitle="还没有成员"
                emptyDescription="账号由管理员创建。"
              />
              <div className="mt-3 flex flex-col gap-1.5">
                {usage.members
                  .filter((member) => member.totalTokens > 0)
                  .slice(0, 5)
                  .map((member) => (
                    <div key={member.userId} className="flex items-center gap-2">
                      <span className="w-24 shrink-0 truncate text-[11px] text-fg-muted">{member.name}</span>
                      <QualityBar
                        value={member.totalTokens}
                        max={usage.members[0]?.totalTokens ?? 1}
                        tone="accent"
                        ariaLabel={`${member.name} 的合计 token`}
                      />
                    </div>
                  ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-1.5 text-[11px] leading-relaxed text-fg-subtle">
              <p>
                <span className="font-medium text-fg-muted">口径说明。</span>
                token 数直接取自模型服务返回的 usage，不做估算。
                「其中思考」是输出 token 里属于模型推理过程的部分，<strong>已包含在输出 token 内</strong>，
                合计时只算输入 + 输出，不重复累加。
              </p>
              <p>
                请求次数与 token 并列展示，因为两者互不可推导：同样一万 token 可能是 1 次完整研判，
                也可能是 3 次短请求。没有拿到模型响应的失败只计次数，token 记为未知。
              </p>
              <p>
                <span className="font-medium text-fg-muted">这张表用于算账与看配额，不作为绩效评价依据。</span>
                {' '}用量高通常意味着在积极验证想法，不是问题；真正该关注的信号是「额度不够用了」或
                「有人反复重试同一个失败」。平台其余部分仍然不做人均产出与排名。
              </p>
            </CardBody>
          </Card>
        </>
      ) : null}
    </PageContainer>
  );
}
